import { useState, useMemo } from "react"
import type { ComponentProps, FormEvent } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import type { CustomerEditMutationVariables } from "@/v2-backoffice-client/customer/edit/__types__/customer.edit.mutation"

type FormData = CustomerEditMutationVariables["input"]

export type CustomerEditFormProps = Omit<ComponentProps<"form">, "onSubmit"> & {
  customer: CustomerFragment
  onSubmit(data: FormData, e: FormEvent): void
}

// App-owned identity fields only (D0). On a linked customer the saved
// values are pushed to the native HubSpot company properties immediately
// (app-wins) — the CRM columns are read-only snapshot data and not here.
function CustomerEditForm(props: CustomerEditFormProps) {
  const { customer, onSubmit, className, ...formProps } = props

  const [formData, setFormData] = useState<FormData>(
    useMemo(() => {
      return {
        Name: customer.Name ?? "",
        OrganizationNumber: customer.OrganizationNumber ?? "",
        Email: customer.Email ?? "",
        Phone: customer.Phone ?? "",
        Website: customer.Website ?? "",
        Street: customer.Street ?? "",
        PostalCode: customer.PostalCode ?? "",
        City: customer.City ?? "",
        Country: customer.Country ?? "",
      }
    }, [customer]),
  )

  function handleChange(field: keyof FormData, value: string) {
    setFormData({ ...formData, [field]: value })
  }

  return (
    <form
      className={cn("customer-edit-form space-y-4", className)}
      onSubmit={(e) => {
        onSubmit(formData, e)
      }}
      {...formProps}
    >
      <div className="space-y-2">
        <Label htmlFor="customerEditName">Name</Label>
        <Input
          id="customerEditName"
          type="text"
          required
          value={formData.Name ?? ""}
          onChange={(e) => {
            handleChange("Name", e.target.value)
          }}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="customerEditOrganizationNumber">Org.nr</Label>
          <Input
            id="customerEditOrganizationNumber"
            type="text"
            value={formData.OrganizationNumber ?? ""}
            onChange={(e) => {
              handleChange("OrganizationNumber", e.target.value)
            }}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="customerEditPhone">Phone</Label>
          <Input
            id="customerEditPhone"
            type="tel"
            value={formData.Phone ?? ""}
            onChange={(e) => {
              handleChange("Phone", e.target.value)
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="customerEditEmail">Email</Label>
          <Input
            id="customerEditEmail"
            type="email"
            value={formData.Email ?? ""}
            onChange={(e) => {
              handleChange("Email", e.target.value)
            }}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="customerEditWebsite">Website</Label>
          <Input
            id="customerEditWebsite"
            type="text"
            value={formData.Website ?? ""}
            onChange={(e) => {
              handleChange("Website", e.target.value)
            }}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="customerEditStreet">Street</Label>
        <Input
          id="customerEditStreet"
          type="text"
          value={formData.Street ?? ""}
          onChange={(e) => {
            handleChange("Street", e.target.value)
          }}
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="customerEditPostalCode">Postal code</Label>
          <Input
            id="customerEditPostalCode"
            type="text"
            value={formData.PostalCode ?? ""}
            onChange={(e) => {
              handleChange("PostalCode", e.target.value)
            }}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="customerEditCity">City</Label>
          <Input
            id="customerEditCity"
            type="text"
            value={formData.City ?? ""}
            onChange={(e) => {
              handleChange("City", e.target.value)
            }}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="customerEditCountry">Country</Label>
          <Input
            id="customerEditCountry"
            type="text"
            value={formData.Country ?? ""}
            onChange={(e) => {
              handleChange("Country", e.target.value)
            }}
          />
        </div>
      </div>
    </form>
  )
}

export default CustomerEditForm
